// @ts-strict-ignore
import { TRANSITION_ELEMENT_ASSETS } from '../app.constants';
import { ClientNode } from '../model/client-model/client-node';
import { ClientTarget } from '../model/client-model/client-target';
import { ModelType } from '../model/client-model/properties/model-type';
import { NodeType } from '../model/client-model/properties/node-type';
import { SubsectorIdentifier } from '../model/enums/emission-icon-identifier';
import { IconColour } from '../model/enums/icon-colour';
import { IconNodeType } from '../model/enums/icon-type';
import { SectorIdentifier } from '../model/enums/sector-identifier';
import { TransitionElement } from '../model/enums/transition-element';
import { TransitionElementIcon } from '../model/enums/transition-element-icon';

export type IconPathIdentifier = SectorIdentifier | SubsectorIdentifier | TransitionElement | 'default' | 'city';

/**
 * Handles getting the path for sectors, subsectors and transition elements icons.
 */
export function getIconPath(identifier: IconPathIdentifier, colour: IconColour = IconColour.Black): string {
  if (typeof identifier === 'string' && identifier.includes('assets/images') && identifier.includes('.svg')) {
    return identifier;
  }
  const isCity = identifier === 'city';
  const isDefault = identifier === 'default';
  const isDefined = identifier !== undefined;
  const isSector = SectorIdentifier[identifier] !== undefined;
  const isSubsector = SubsectorIdentifier[identifier] !== undefined;
  const isTE = TransitionElementIcon[identifier] !== undefined;

  if (!isDefined || !(isSector || isSubsector || isTE || isDefault || isCity)) {
    return `${TRANSITION_ELEMENT_ASSETS}icons/-.svg`;
  }

  const iconNodeType = isSector
    ? IconNodeType.Sector
    : isSubsector || isCity
      ? IconNodeType.Subsector
      : isTE || isDefault
        ? IconNodeType.TransitionElement
        : IconNodeType.TransitionElement;
  const iconIdentifier = identifier;
  const iconColour = colour === IconColour.Black ? '' : '.' + IconColour.White;

  if (iconNodeType === IconNodeType.TransitionElement) {
    return `${TRANSITION_ELEMENT_ASSETS}icons/${iconIdentifier}${iconColour}.svg`;
  } else {
    return `/assets/images/${iconNodeType}/${iconIdentifier}${iconColour}.svg`;
  }
}

export function getIconPathIdentifier(
  target: Omit<ClientTarget, 'indicator'>,
  nodes: ClientNode[]
): IconPathIdentifier {
  if (target.nodeProperties.transitionElement) {
    return target.nodeProperties.transitionElement;
  }

  const node = nodes?.find((n) => n.id === target.id);
  if (node?.childNodes.length) {
    const supplyTargetChildNode = nodes.find((n) => {
      const isSupplyTarget = n.nodeProperties?.type === NodeType.SupplyTarget;
      const isChild = node.childNodes.includes(n.id);
      return isSupplyTarget && isChild;
    });

    return supplyTargetChildNode?.nodeProperties.transitionElement;
  }

  if (target.nodeProperties.modelType === ModelType.CUSTOM) {
    return 'default';
  }

  if (target.nodeProperties.icon) {
    return target.nodeProperties.icon as IconPathIdentifier;
  }

  return undefined;
}
